import * as _ from 'lodash';
import {useMemo, useEffect, useRef, useState, useContext} from 'react';
import {useApolloClient as useApolloApolloClient} from '@apollo/react-hooks';
import {
  useDispatch as useReduxDispatch,
  useSelector as useReduxSelector,
  shallowEqual,
} from 'react-redux';
import {Selector} from 'reselect';
import {Dispatch, RootState} from '../types/redux';

import * as Types from './types';
import {PanelBankContext} from './panelbank/context';
import {envIsDev} from '../config';
import {difference} from '../util/data';

// This is a nicer way to make reselect selectors that depend on props
// work with hooks. We recreate the hook whenever the passed in args
// change. You'd typically unpack the props you want and pass them
// in as args.
export const usePropsSelector = <Args extends any[], R>(
  selectorFactory: (...args: Args) => Selector<RootState, R>,
  ...args: Args
) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selector = useMemo(() => selectorFactory(...args), args);
  return useReduxSelector(selector, shallowEqual);
};

export const useDispatch = () => {
  return useReduxDispatch<Dispatch>();
};

// From stackoverflow
export const usePrevious = <T extends any>(value: T) => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

// Only return a new value if value changes by deep-comparison
// from one call to the next.
export const useDeepMemo = <T extends any>(
  value: T,
  equalityFn?: (a: T, b: T | undefined) => boolean
) => {
  equalityFn = equalityFn ?? _.isEqual;
  const ref = useRef<T>();
  const prev = usePrevious(value);
  if (!equalityFn(value, prev)) {
    ref.current = value;
  }
  return ref.current as T;
};

type UseSelector = <TSelected>(
  selector: (state: RootState) => TSelected,
  equalityFn?: (left: TSelected, right: TSelected) => boolean,
  debugStr?: string
) => TSelected;

const useSelectorInner: UseSelector = (selector, equalityFn, debugStr) =>
  useReduxSelector(selector, equalityFn);

const useSelectorInnerWithDebug: UseSelector = (
  selector,
  equalityFn = (a, b) => a === b,
  debugStr
) => {
  const result = useSelectorInner(selector, equalityFn);

  const prevResult = usePrevious(result);
  if (debugStr && !equalityFn(prevResult!, result)) {
    console.group(`[useSelector]: ${debugStr} returned different result`);
    console.log('prev', prevResult);
    console.log('next', result);
    console.log('diff', difference(prevResult, result));
    console.groupEnd();
  }

  return result;
};

export const useSelector = envIsDev
  ? useSelectorInnerWithDebug
  : useSelectorInner;

export const useMemoizedSelector: UseSelector = <T>(
  selector: (state: RootState) => T,
  equalityFn: (left: T, right: T) => boolean = (a, b) => a === b,
  debugStr?: string
) => {
  const resultRef = useRef<T>();
  const memoizedSelector = useMemo(
    () => (state: RootState) => {
      const result = selector(state);
      if (resultRef.current == null || !equalityFn(resultRef.current, result)) {
        resultRef.current = result;
      }
      return resultRef.current;
    },
    [selector, equalityFn]
  );
  return useSelector(memoizedSelector, undefined, debugStr);
};

// From https://usehooks.com/useOnScreen/
export function useOnScreen(
  domRef: React.MutableRefObject<Element | null>,
  rootMargin: string = '0px'
) {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
      }
    );
    const domRefValue = domRef.current;
    if (domRefValue) {
      observer.observe(domRefValue);
    }
    return () => {
      if (domRefValue) {
        observer.unobserve(domRefValue);
      }
    };
  }, [domRef, rootMargin]); // Empty array ensures that effect is only run on mount and unmount

  return isIntersecting;
}

// Returns true when domRef becomes onScreen for the first time, and stays
// true until value changes.
export function useWhenOnScreenAfterNewValue(
  domRef: React.MutableRefObject<Element | null>,
  value: any
) {
  const onScreen = useOnScreen(domRef);
  const prevValue = usePrevious(value);
  // TODO: it would be nice to be able to init ready = true
  // but onScreen always starts false currently
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (onScreen) {
      if (!ready) {
        setReady(true);
      }
      return;
    }
    if (value !== prevValue && !onScreen) {
      setReady(false);
    }
  }, [onScreen, ready, prevValue, value]);
  return ready;
}

/* Returns true after a ref is on screen for the first time */
export function useWaitToLoadTilOnScreen(
  domRef: React.MutableRefObject<Element | null>
) {
  const elementPageYOffset = domRef.current
    ? (window.pageYOffset || document.documentElement.scrollTop) +
      domRef.current.getBoundingClientRect().top
    : null;
  // Always load content near the top of the page immediately.
  const isAboveFold =
    elementPageYOffset != null ? elementPageYOffset < 1000 : false;
  const [hasRendered, setHasRendered] = useState(false);
  const onScreenTimer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const isOnScreen = useOnScreen(domRef, '300px');
  const {disableWaitToLoad} = useContext(PanelBankContext);

  useEffect(() => {
    if (hasRendered) {
      return;
    }
    if (isAboveFold || disableWaitToLoad) {
      setHasRendered(true);
    }
    if (!onScreenTimer.current && isOnScreen) {
      onScreenTimer.current = setTimeout(() => {
        setHasRendered(true);
      }, 200);
    } else if (onScreenTimer.current && !isOnScreen) {
      clearTimeout(onScreenTimer.current);
      onScreenTimer.current = undefined;
    }
  }, [hasRendered, isAboveFold, isOnScreen, disableWaitToLoad]);

  if (hasRendered) {
    return true;
  }

  return false;
}

export const useApolloClient: () => Types.ApolloClient = () => {
  return useApolloApolloClient() as Types.ApolloClient;
};

export const useGatedValue = <T extends any>(
  value: T,
  updateWhen: (val: T) => boolean
) => {
  const ref = useRef<T>(value);
  if (value !== ref.current && updateWhen(value)) {
    ref.current = value;
  }
  return ref.current;
};
